import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useParams, Link, Redirect } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCheck,
  FaCopy,
  FaEdit,
  FaListUl,
  FaPlus,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaUsers,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import COLORS from "../resources/colors/COLORS";
import BackButton from "../ChildComponent/BackButton";
import MdlSelectClass from "../dialog/MdlSelectClass";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function classLabel(item) {
  if (!item) return "Class";
  if (item.section_id === "No" || !item.section_name) {
    return item.class_name || "Class";
  }
  return `${item.class_name}: ${item.section_name}`;
}

//Created By Cursor
function normalizeSectionId(sectionId) {
  if (
    sectionId === "null" ||
    sectionId === null ||
    sectionId === undefined ||
    sectionId === ""
  ) {
    return "No";
  }
  return sectionId;
}

//Created By Cursor
function formatDateToDDMMYYYY(dateString) {
  if (!dateString) return "";
  if (dateString.includes("-") && dateString.split("-")[0].length === 2) {
    return dateString;
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

//Created By Cursor
function formatDateToYYYYMMDD(dateString) {
  if (!dateString) return "";
  if (dateString.includes("-") && dateString.split("-")[0].length === 4) {
    return dateString;
  }
  const parts = dateString.split("-");
  if (parts.length === 3 && parts[0].length === 2) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateString;
}

const ExamDateSheet = () => {
  const { exam_id, session_id, exam_name, exam_status } = useParams();
  const loggedIn = localStorage.getItem("token") !== null;

  const [bootLoading, setBootLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sheetLoading, setSheetLoading] = useState(false);

  const [EXAM_ID] = useState(exam_id || "");
  const [SESSION_ID] = useState(session_id || "");
  const [EXAM_NAME] = useState(decodeURIComponent(exam_name || ""));
  const [EXAM_STATUS] = useState(decodeURIComponent(exam_status || ""));

  const [mClassList, setMClassList] = useState([]);
  const [CLASS_SELECTED_POSITION, setClassSelectedPosition] = useState(0);
  const [CLASS_ID, setClassId] = useState("");
  const [SECTION_ID, setSectionId] = useState("");

  const [mDateSheet, setMDateSheet] = useState([]);
  const [mSubject, setMSubject] = useState([]);
  const [mSubjectPaper, setMSubjectPaper] = useState([]);

  const [examShift, setExamShift] = useState("");
  const [inputExamDate, setInputExamDate] = useState("");
  const [inputExamTime, setInputExamTime] = useState("");
  const [selectedSubjectId, setSelectedSubjectId] = useState("");
  const [selectedPaperId, setSelectedPaperId] = useState("");
  const [shiftTimeCache, setShiftTimeCache] = useState({});

  const [editingDateSheet, setEditingDateSheet] = useState(null);
  const [editDateSheetData, setEditDateSheetData] = useState({
    exam_date: "",
    exam_time: "",
    exam_shift: "",
    subject_id: "",
    subject_paper_id: "",
  });

  const [isOpenSelectClass, setIsOpenSelectClass] = useState(false);
  const classPositionRef = useRef(0);

  //Created By Cursor
  const applyClassSelection = useCallback((classList, position) => {
    if (!classList.length || !classList[position]) {
      setClassId("");
      setSectionId("");
      setMSubject([]);
      setMSubjectPaper([]);
      setSelectedSubjectId("");
      setSelectedPaperId("");
      setMDateSheet([]);
      return;
    }

    const selected = classList[position];
    setClassId(selected.class_id || "");
    setSectionId(normalizeSectionId(selected.section_id));

    const subjects = selected.subject || [];
    setMSubject(subjects);

    if (subjects.length > 0) {
      const firstSubject = subjects[0];
      setSelectedSubjectId(String(firstSubject.subject_id));
      const papers = (selected.subject_paper || []).filter(
        (paper) => paper.subject_id === firstSubject.subject_id
      );
      setMSubjectPaper(papers);
      setSelectedPaperId(
        papers.length > 0 ? String(papers[0].exam_paper_id) : ""
      );
    } else {
      setSelectedSubjectId("");
      setMSubjectPaper([]);
      setSelectedPaperId("");
    }

    setExamShift("");
    setInputExamDate("");
    setInputExamTime("");
    setMDateSheet([]);
  }, []);

  //Created By Cursor
  const getExamParticipatingClass = useCallback(
    async ({ soft = false, preserveSelection = false } = {}) => {
      if (!EXAM_ID || !SESSION_ID) {
        setMClassList([]);
        setBootLoading(false);
        setRefreshing(false);
        return;
      }

      if (soft) setRefreshing(true);
      else setBootLoading(true);

      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code is missing.");
        setBootLoading(false);
        setRefreshing(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE()}getClassWithSubjectsPartAndPaper.php`,
          {
            params: {
              school_code: schoolCode,
              exam_id: EXAM_ID,
              session_id: SESSION_ID,
            },
          }
        );

        if (response.data?.api_status === "OK") {
          const classList = response.data.data || [];
          setMClassList(classList);
          if (!preserveSelection) {
            setClassSelectedPosition(0);
            classPositionRef.current = 0;
          }
        } else {
          setMClassList([]);
          myToast.notify_warning(
            response.data?.api_remark || "Could not load participating classes."
          );
        }
      } catch (error) {
        console.error("Error fetching participating classes:", error);
        setMClassList([]);
        myToast.notify_warning("Network error loading date sheet classes.");
      } finally {
        setBootLoading(false);
        setRefreshing(false);
      }
    },
    [EXAM_ID, SESSION_ID, applyClassSelection]
  );

  //Created By Cursor
  const getExamSheet = useCallback(async () => {
    if (!CLASS_ID || !SECTION_ID) return;

    setSheetLoading(true);
    const schoolCode = SessionManager.getSchoolCode();

    try {
      const response = await axios.get(`${API_BASE()}getExamDateSheet.php`, {
        params: {
          school_code: schoolCode,
          exam_id: EXAM_ID,
          session_id: SESSION_ID,
          class_id: CLASS_ID,
          section_id: SECTION_ID,
        },
      });

      if (response.data?.api_status === "OK") {
        const rows = response.data.data || [];
        setMDateSheet(rows);

        if (rows.length > 0) {
          setShiftTimeCache((prev) => {
            const cache = { ...prev };
            rows.forEach((item) => {
              if (item.exam_shift && item.exam_time && !cache[item.exam_shift]) {
                cache[item.exam_shift] = item.exam_time;
              }
            });
            return cache;
          });
        }
      } else {
        setMDateSheet([]);
      }
    } catch (error) {
      console.error("Error fetching date sheet:", error);
      setMDateSheet([]);
    } finally {
      setSheetLoading(false);
    }
  }, [CLASS_ID, SECTION_ID, EXAM_ID, SESSION_ID]);

  useEffect(() => {
    window.scrollTo(0, 0);
    getExamParticipatingClass();
  }, [getExamParticipatingClass]);

  useEffect(() => {
    classPositionRef.current = CLASS_SELECTED_POSITION;
    const position = Math.min(
      CLASS_SELECTED_POSITION,
      Math.max(mClassList.length - 1, 0)
    );
    applyClassSelection(mClassList, position);
  }, [CLASS_SELECTED_POSITION, mClassList, applyClassSelection]);

  useEffect(() => {
    if (!CLASS_ID || !SECTION_ID) return;
    getExamSheet();
  }, [CLASS_ID, SECTION_ID, getExamSheet]);

  const selectedClass = mClassList[CLASS_SELECTED_POSITION] || null;

  const hasOtherClasses = useMemo(
    () => mClassList.length > 1,
    [mClassList.length]
  );

  const kpi = useMemo(
    () => ({
      classCount: mClassList.length,
      subjectCount: mSubject.length,
      entryCount: mDateSheet.length,
    }),
    [mClassList.length, mSubject.length, mDateSheet.length]
  );

  //Created By Cursor
  const handleShiftChange = useCallback(
    (event) => {
      const selectedShift = event.target.value;
      setExamShift(selectedShift);
      if (shiftTimeCache[selectedShift]) {
        setInputExamTime(shiftTimeCache[selectedShift]);
      } else {
        setInputExamTime("");
      }
    },
    [shiftTimeCache]
  );

  //Created By Cursor
  const handleSubjectChange = useCallback(
    (event) => {
      const subjectId = event.target.value;
      setSelectedSubjectId(subjectId);

      const papers = (selectedClass?.subject_paper || []).filter(
        (paper) => String(paper.subject_id) === String(subjectId)
      );
      setMSubjectPaper(papers);
      setSelectedPaperId(
        papers.length > 0 ? String(papers[0].exam_paper_id) : ""
      );
    },
    [selectedClass]
  );

  //Created By Cursor
  const isValidInput = useCallback(() => {
    if (!inputExamDate) {
      myToast.notify_warning("Enter exam date.");
      return false;
    }
    if (!inputExamTime) {
      myToast.notify_warning("Enter exam time.");
      return false;
    }
    if (!selectedSubjectId) {
      myToast.notify_warning("Select a subject.");
      return false;
    }
    if (!selectedPaperId) {
      myToast.notify_warning("Select a subject paper.");
      return false;
    }
    return true;
  }, [inputExamDate, inputExamTime, selectedSubjectId, selectedPaperId]);

  //Created By Cursor
  const addExamDateSheet = useCallback(async () => {
    if (!isValidInput()) return;

    setSheetLoading(true);
    const schoolCode = SessionManager.getSchoolCode();
    const shift = examShift || "";

    try {
      const response = await axios.get(`${API_BASE()}AddExamDateSheet.php`, {
        params: {
          session_id: SESSION_ID,
          exam_id: EXAM_ID,
          class_id: CLASS_ID,
          section_id: SECTION_ID,
          subject_id: selectedSubjectId,
          subject_paper_id: selectedPaperId,
          exam_date: inputExamDate,
          exam_time: inputExamTime,
          exam_shift: shift,
          school_code: schoolCode,
        },
      });

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Exam date sheet added successfully.");
        if (shift && inputExamTime) {
          setShiftTimeCache((prev) => ({ ...prev, [shift]: inputExamTime }));
        }
        setInputExamDate("");
        setInputExamTime("");
        getExamSheet();
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Could not add date sheet entry."
        );
        setSheetLoading(false);
      }
    } catch (error) {
      console.error("Error adding date sheet:", error);
      myToast.notify_error("Network error adding date sheet.");
      setSheetLoading(false);
    }
  }, [
    isValidInput,
    examShift,
    SESSION_ID,
    EXAM_ID,
    CLASS_ID,
    SECTION_ID,
    selectedSubjectId,
    selectedPaperId,
    inputExamDate,
    inputExamTime,
    getExamSheet,
  ]);

  //Created By Cursor
  const deleteExamDateSheet = useCallback(
    async (datesheetId) => {
      setSheetLoading(true);
      const schoolCode = SessionManager.getSchoolCode();
      const staffId = SessionManager.getStaffDetails()?.staff_id;

      try {
        const response = await axios.get(
          `${API_BASE()}deleteExamDateSheet.php`,
          {
            params: {
              session_id: SESSION_ID,
              school_code: schoolCode,
              exam_id: EXAM_ID,
              datesheet_id: datesheetId,
              staff_id: staffId,
            },
          }
        );

        if (response.data?.api_status === "OK") {
          myToast.notify_success("Exam date sheet deleted successfully.");
          getExamSheet();
        } else {
          myToast.notify_warning(
            response.data?.api_remark || "Could not delete date sheet entry."
          );
          setSheetLoading(false);
        }
      } catch (error) {
        console.error("Error deleting date sheet:", error);
        myToast.notify_error("Network error deleting date sheet.");
        setSheetLoading(false);
      }
    },
    [SESSION_ID, EXAM_ID, getExamSheet]
  );

  //Created By Cursor
  const startEditDateSheet = useCallback((item) => {
    setEditingDateSheet(item.datesheet_id);
    setEditDateSheetData({
      exam_date: formatDateToDDMMYYYY(item.exam_date),
      exam_time: item.exam_time,
      exam_shift: item.exam_shift,
      subject_id: item.subject_id,
      subject_paper_id: item.subject_paper_id,
    });
  }, []);

  //Created By Cursor
  const cancelEdit = useCallback(() => {
    setEditingDateSheet(null);
    setEditDateSheetData({
      exam_date: "",
      exam_time: "",
      exam_shift: "",
      subject_id: "",
      subject_paper_id: "",
    });
  }, []);

  //Created By Cursor
  const updateExamDateSheet = useCallback(async () => {
    if (!editDateSheetData.exam_date || !editDateSheetData.exam_time) {
      myToast.notify_warning("Enter exam date and time.");
      return;
    }

    setSheetLoading(true);
    const schoolCode = SessionManager.getSchoolCode();

    try {
      const response = await axios.get(
        `${API_BASE()}updateExamDateSheet.php`,
        {
          params: {
            school_code: schoolCode,
            section_id: SECTION_ID,
            session_id: SESSION_ID,
            exam_id: EXAM_ID,
            class_id: CLASS_ID,
            subject_id: editDateSheetData.subject_id,
            subject_paper_id: editDateSheetData.subject_paper_id,
            exam_date: editDateSheetData.exam_date,
            exam_shift: editDateSheetData.exam_shift,
            exam_time: editDateSheetData.exam_time,
          },
        }
      );

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Exam date sheet updated successfully.");
        if (editDateSheetData.exam_shift && editDateSheetData.exam_time) {
          setShiftTimeCache((prev) => ({
            ...prev,
            [editDateSheetData.exam_shift]: editDateSheetData.exam_time,
          }));
        }
        cancelEdit();
        getExamSheet();
      } else {
        myToast.notify_error(
          response.data?.api_remark || "Error updating date sheet."
        );
        setSheetLoading(false);
      }
    } catch (error) {
      console.error("Error updating date sheet:", error);
      myToast.notify_error("Network error updating date sheet.");
      setSheetLoading(false);
    }
  }, [
    editDateSheetData,
    SECTION_ID,
    SESSION_ID,
    EXAM_ID,
    CLASS_ID,
    cancelEdit,
    getExamSheet,
  ]);

  //Created By Cursor
  const refreshSoft = useCallback(() => {
    getExamParticipatingClass({ soft: true, preserveSelection: true });
    getExamSheet();
  }, [getExamParticipatingClass, getExamSheet]);

  if (!loggedIn) return <Redirect to="/Login" />;
  if (bootLoading) return <MyLoader />;

  const detailsPath = `/AdminMain/ExamDetails/${EXAM_ID}/${SESSION_ID}/${encodeURIComponent(
    EXAM_NAME || ""
  )}/${encodeURIComponent(EXAM_STATUS || "")}`;

  return (
    <div
      className="content payroll-page sm-page eds-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Exam Date Sheet</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaCalendarAlt />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Date Sheet</h1>
              <p className="sm-hero__sub">
                {EXAM_NAME || "Exam"} · Schedule exam dates & timings
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            {EXAM_STATUS ? (
              <span className="eds-status-pill" title="Exam status">
                {EXAM_STATUS}
              </span>
            ) : null}
            <button
              type="button"
              onClick={refreshSoft}
              disabled={refreshing || sheetLoading}
              className={`pp-icon-btn${refreshing ? " spinning" : ""}`}
              title="Refresh"
              aria-label="Refresh"
            >
              <FaSyncAlt aria-hidden />
            </button>
            <Link to={detailsPath} className="eds-hero-link">
              Exam Details
            </Link>
            <Link to="/AdminMain/ExamList" className="eds-hero-link">
              Exam List
            </Link>
          </div>
        </header>

        <div className="eds-content">
          <div className="eds-kpi-row">
            <span className="eds-kpi">
              <FaUsers aria-hidden />
              {kpi.classCount} classes
            </span>
            <span className="eds-kpi">
              <FaListUl aria-hidden />
              {kpi.subjectCount} subjects
            </span>
            <span className="eds-kpi">
              <FaCalendarAlt aria-hidden />
              {kpi.entryCount} entries
            </span>
          </div>

          <section className="eds-section-card" aria-label="Participating classes">
            <div className="eds-section-card__header">
              <h2 className="eds-section-card__title">Participating Classes</h2>
              <span className="eds-section-card__count">{mClassList.length}</span>
            </div>
            <div className="eds-section-card__body">
              {mClassList.length === 0 ? (
                <div className="eds-empty">
                  <p className="eds-empty__title">No participating classes</p>
                  <p className="eds-empty__hint">
                    Add classes from Exam Details first.
                  </p>
                </div>
              ) : (
                <div className="eds-chip-grid">
                  {mClassList.map((classItem, index) => (
                    <button
                      key={`${classItem.class_id}-${classItem.section_id}-${index}`}
                      type="button"
                      className={`eds-chip${
                        CLASS_SELECTED_POSITION === index ? " is-active" : ""
                      }`}
                      onClick={() => setClassSelectedPosition(index)}
                    >
                      {classLabel(classItem)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="eds-section-card" aria-label="Add date sheet entry">
            <div className="eds-section-card__header">
              <h2 className="eds-section-card__title">
                Add Entry · {classLabel(selectedClass)}
              </h2>
              <div className="eds-header-actions">
                <button
                  type="button"
                  className="eds-header-btn"
                  disabled={!hasOtherClasses}
                  onClick={() => setIsOpenSelectClass(true)}
                  title={
                    hasOtherClasses
                      ? "Copy date sheet from another class"
                      : "No other classes available"
                  }
                >
                  <FaCopy aria-hidden /> Copy From Class
                </button>
              </div>
            </div>
            <div className="eds-section-card__body">
              <div className="eds-form-grid">
                <label className="eds-field">
                  <span className="eds-field__label">Exam Shift</span>
                  <select
                    className="eds-input"
                    value={examShift}
                    onChange={handleShiftChange}
                  >
                    <option value="">Select Shift</option>
                    <option value="First">First</option>
                    <option value="Second">Second</option>
                  </select>
                </label>

                <label className="eds-field">
                  <span className="eds-field__label">Exam Date</span>
                  <input
                    className="eds-input"
                    type="date"
                    value={
                      inputExamDate ? formatDateToYYYYMMDD(inputExamDate) : ""
                    }
                    onChange={(e) =>
                      setInputExamDate(formatDateToDDMMYYYY(e.target.value))
                    }
                  />
                  {inputExamDate ? (
                    <span className="eds-field__hint">{inputExamDate}</span>
                  ) : null}
                </label>

                <label className="eds-field">
                  <span className="eds-field__label">Exam Time</span>
                  <input
                    className="eds-input"
                    type="text"
                    value={inputExamTime}
                    onChange={(e) => setInputExamTime(e.target.value)}
                    placeholder="e.g. 09:00 AM - 12:00 PM"
                  />
                </label>

                <label className="eds-field">
                  <span className="eds-field__label">Subject</span>
                  <select
                    className="eds-input"
                    value={selectedSubjectId}
                    onChange={handleSubjectChange}
                    disabled={!mSubject.length}
                  >
                    {mSubject.map((item) => (
                      <option key={item.subject_id} value={item.subject_id}>
                        {item.subject_name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="eds-field">
                  <span className="eds-field__label">Subject Paper</span>
                  <select
                    className="eds-input"
                    value={selectedPaperId}
                    onChange={(e) => setSelectedPaperId(e.target.value)}
                    disabled={!mSubjectPaper.length}
                  >
                    {mSubjectPaper.map((item) => (
                      <option key={item.exam_paper_id} value={item.exam_paper_id}>
                        {item.exam_paper_name} {item.paper_type}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="eds-form-action">
                  <button
                    type="button"
                    className="eds-add-btn"
                    onClick={addExamDateSheet}
                    disabled={sheetLoading || !mSubject.length}
                  >
                    <FaPlus aria-hidden /> Add Entry
                  </button>
                </div>
              </div>
            </div>
          </section>

          <section className="eds-section-card" aria-label="Date sheet preview">
            <div className="eds-section-card__header">
              <h2 className="eds-section-card__title">Date Sheet Preview</h2>
              <span className="eds-section-card__count">{mDateSheet.length}</span>
            </div>
            <div className="eds-section-card__body">
              {sheetLoading && mDateSheet.length === 0 ? (
                <div className="eds-loading-inline">
                  <MyLoader />
                </div>
              ) : mDateSheet.length === 0 ? (
                <div className="eds-empty eds-empty--dashed">
                  <FaCalendarAlt className="eds-empty__icon" aria-hidden />
                  <p className="eds-empty__title">No date sheet added yet</p>
                  <p className="eds-empty__hint">
                    Add your first exam date sheet entry using the form above.
                  </p>
                </div>
              ) : (
                <div
                  className={`eds-table-wrap${sheetLoading ? " is-busy" : ""}`}
                >
                  <table className="eds-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Examination Date</th>
                        <th>Timing</th>
                        <th>Shift</th>
                        <th>Subject</th>
                        <th>Subject Paper</th>
                        <th>Paper Type</th>
                        <th className="eds-col-action">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mDateSheet.map((item, index) => {
                        const isEditing =
                          editingDateSheet === item.datesheet_id;
                        return (
                          <tr
                            key={item.datesheet_id || index}
                            className={index % 2 === 1 ? "is-even" : ""}
                          >
                            <td className="eds-col-num">{index + 1}</td>
                            <td>
                              {isEditing ? (
                                <input
                                  className="eds-input eds-input--inline"
                                  type="date"
                                  value={
                                    editDateSheetData.exam_date
                                      ? formatDateToYYYYMMDD(
                                          editDateSheetData.exam_date
                                        )
                                      : ""
                                  }
                                  onChange={(e) =>
                                    setEditDateSheetData({
                                      ...editDateSheetData,
                                      exam_date: formatDateToDDMMYYYY(
                                        e.target.value
                                      ),
                                    })
                                  }
                                />
                              ) : (
                                item.exam_date
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <input
                                  className="eds-input eds-input--inline"
                                  type="text"
                                  value={editDateSheetData.exam_time}
                                  onChange={(e) =>
                                    setEditDateSheetData({
                                      ...editDateSheetData,
                                      exam_time: e.target.value,
                                    })
                                  }
                                />
                              ) : (
                                item.exam_time
                              )}
                            </td>
                            <td>
                              {isEditing ? (
                                <select
                                  className="eds-input eds-input--inline"
                                  value={editDateSheetData.exam_shift}
                                  onChange={(e) =>
                                    setEditDateSheetData({
                                      ...editDateSheetData,
                                      exam_shift: e.target.value,
                                    })
                                  }
                                >
                                  <option value="First">First</option>
                                  <option value="Second">Second</option>
                                </select>
                              ) : (
                                item.exam_shift
                              )}
                            </td>
                            <td>{item.subject_name}</td>
                            <td>{item.exam_paper_name}</td>
                            <td>{item.paper_type}</td>
                            <td className="eds-col-action">
                              {isEditing ? (
                                <div className="eds-row-actions">
                                  <button
                                    type="button"
                                    className="eds-action-btn eds-action-btn--save"
                                    onClick={updateExamDateSheet}
                                  >
                                    <FaCheck aria-hidden /> Save
                                  </button>
                                  <button
                                    type="button"
                                    className="eds-action-btn eds-action-btn--cancel"
                                    onClick={cancelEdit}
                                  >
                                    <FaTimes aria-hidden /> Cancel
                                  </button>
                                </div>
                              ) : (
                                <div className="eds-row-actions">
                                  <button
                                    type="button"
                                    className="eds-action-btn eds-action-btn--edit"
                                    onClick={() => startEditDateSheet(item)}
                                  >
                                    <FaEdit aria-hidden /> Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="eds-action-btn eds-action-btn--delete"
                                    onClick={() =>
                                      deleteExamDateSheet(item.datesheet_id)
                                    }
                                  >
                                    <FaTrash aria-hidden /> Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </section>
        </div>

        {isOpenSelectClass ? (
          <MdlSelectClass
            isOpen={isOpenSelectClass}
            onClose={() => setIsOpenSelectClass(false)}
            onSelect={() => {
              getExamSheet();
              setIsOpenSelectClass(false);
            }}
            exam_id={EXAM_ID}
            session_id={SESSION_ID}
            current_class_id={CLASS_ID}
            current_section_id={SECTION_ID}
            copyType="datesheets"
          />
        ) : null}
      </div>
    </div>
  );
};

export default ExamDateSheet;
